import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { RootState } from '../../../store/store';
import SettingsCommonCard from '../../../ui/cards/SettingsCommonCard';
import SubPageInnerContentHolder from '../../../components/subPageContentHolder/SubPageInnerContentHolder';
import { HOME_ROUTE } from '../../../lib/constants/route';
import { questionSVG, simpleCreditCardSVG } from '../../../lib/allCustomSVGs';
import '../../../styles/pages/settings/settings.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import LoadingSpinner from '../../../ui/loadingSpinner/LoadingSpinner';
import { setMessage } from '../../../store/reducers/appSlice';
import isNotEmptyString from '../../../lib/helper-functions/isNotEmptyString';
import { request_package_subscription } from '../../../lib/api/ai-tracker/subscriptions/make_package_subscriptions_api_call';
import { getCheckoutFailureMessage } from '../../../lib/api/ai-tracker/subscriptions/subscription-error-message';
import manage_package_subscriptions_api_call from '../../../lib/api/ai-tracker/subscriptions/manage_package_subscriptions_api_call';
import get_cancel_flow_options_api_call, {
  CancelFlowOption,
} from '../../../lib/api/subscriptions/get_cancel_flow_options_api_call';
import process_cancel_flow_action_api_call from '../../../lib/api/subscriptions/process_cancel_flow_action_api_call';
import submit_cancel_flow_survey_api_call from '../../../lib/api/subscriptions/submit_cancel_flow_survey_api_call';
import SubscriptionPackages from '../../../components/SubscriptionPackages/SubscriptionPackages';
import AgencyPackageItem from '../../../components/SubscriptionPackages/agency/AgencyPackageItem';
import { tara_agency_pricing_hierarchy } from '../../../lib/constants/tara_pricing_plans';
import Popup from '../../../ui/popup/Popup';
import getCurrencySymbol from '../../../lib/helper-functions/getCurrencySymbol';
import HELP_LINKS from '../../../lib/constants/helpLinks';
import { APP_ENV } from '../../../lib/constants/central-api';
import { RoleGate } from '../../../lib/rbac';
import BillingUsageSection from './BillingUsageSection';
import PlanLockNotice from '../../../components/planLock/PlanLockNotice';
import { selectIsPlanLocked } from '../../../lib/plan-lock/plan-lock-rules';
import { usePaymentConfirmation } from '../../../lib/plan-lock/usePaymentConfirmation';
import BillingEmailField from './BillingEmailField';
import refresh_tara_subscription_quietly from '../../../lib/api/tara/refresh_tara_subscription_quietly';

// After a return from checkout the plan is asked for this many times, this
// far apart, before the page says it will appear shortly.
export const CHECKOUT_RETURN_ATTEMPTS = 6;
export const CHECKOUT_RETURN_INTERVAL_MS = 2500;

type CheckoutReturnState = 'none' | 'waiting' | 'timedOut';

const removeQueryParameter = (name: string) => {
  const queryParameters = new URLSearchParams(window.location.search);
  queryParameters.delete(name);
  window.history.replaceState(
    {},
    '',
    `${window.location.pathname}${
      queryParameters.toString() ? `?${queryParameters}` : ''
    }`
  );
};

const SubscriptionSettings = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();

  const isWhiteLabel = useSelector((state: RootState) => state.UI.isWhiteLabel);
  // No active plan or trial: this page is then the only one open, and it
  // offers the plans instead of the details of a plan that no longer runs.
  const isPlanLocked = useSelector(selectIsPlanLocked);
  // The checkout returns to this page with `?orderId=` (a Stripe session id)
  // after payment, or `?checkout=cancelled`. Until a payment is confirmed the
  // plans are not offered again.
  const [returnedFromCheckout] = useState<boolean>(() =>
    new URLSearchParams(window.location.search).has('orderId')
  );
  const payment = usePaymentConfirmation(returnedFromCheckout);
  // A locked organisation waits through the plan-lock check above; one with
  // a plan or trial running waits here until the paid plan shows.
  const [checkoutReturn, setCheckoutReturn] = useState<CheckoutReturnState>(
    () => (returnedFromCheckout && !isPlanLocked ? 'waiting' : 'none')
  );
  const isPaymentPending =
    payment.state !== 'idle' || checkoutReturn !== 'none';
  const isSwitchToAnnualInFlight = useRef(false);
  const availableTaraSubscriptionPackages = useSelector(
    (state: RootState) => state.user?.shop?.availableTaraSubscriptionPackages
  );
  const taraSubscriptionDetails: any = useSelector(
    (state: RootState) => state.user?.shop?.taraSubscriptionDetails
  );

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [activeTaraPackage, setActiveTaraPackage] = useState<any>(null);
  const [showChangePlan, setShowChangePlan] = useState<boolean>(false);
  const [isManageLoading, setIsManageLoading] = useState<boolean>(false);
  const [isSwitchToAnnualLoading, setIsSwitchToAnnualLoading] =
    useState<boolean>(false);
  const [switchToAnnualError, setSwitchToAnnualError] = useState<string | null>(
    null
  );
  const [isCancelSubscriptionLoading, setIsCancelSubscriptionLoading] =
    useState<boolean>(false);
  const [showCancelFlow, setShowCancelFlow] = useState<boolean>(false);
  const [cancelFlowOptions, setCancelFlowOptions] = useState<
    CancelFlowOption[]
  >([]);
  const [surveyCancelFlowOptions, setSurveyCancelFlowOptions] = useState<
    CancelFlowOption[]
  >([]);
  const [activeCancelFlowIndex, setActiveCancelFlowIndex] = useState<number>(0);
  const [cancelFlowError, setCancelFlowError] = useState<string | null>(null);
  const [isShowingQuestionnaire, setIsShowingQuestionnaire] =
    useState<boolean>(false);
  const [selectedCancelReason, setSelectedCancelReason] = useState<string>('');
  const [questionnaireStage, setQuestionnaireStage] = useState<
    | 'questionnaire'
    | 'survey_offer'
    | 'support'
    | 'other_tools'
    | 'missing_features'
    | 'other'
    | 'final'
  >('questionnaire');
  const [activeSurveyOption, setActiveSurveyOption] =
    useState<CancelFlowOption | null>(null);
  const [otherToolsReason, setOtherToolsReason] = useState<string>('');
  const [otherReason, setOtherReason] = useState<string>('');
  const [finalFeedback, setFinalFeedback] = useState<string>('');
  const [satisfactionRating, setSatisfactionRating] = useState<number | null>(
    null
  );
  const [isTriggeringCancellation, setIsTriggeringCancellation] =
    useState<boolean>(false);
  const [isProcessingFollowUpAction, setIsProcessingFollowUpAction] =
    useState<boolean>(false);

  useEffect(() => {
    setIsLoading(true);

    const allPackages =
      availableTaraSubscriptionPackages?.structuredPackageData || [];

    // Match against every known package, seller and agency alike: only the
    // agency plan is offered, but existing seller subscriptions still show.
    const activeSlug = taraSubscriptionDetails?.package_slug;

    const matchedActivePkg = allPackages.find(
      (pkg: any) =>
        pkg.monthly_slug === activeSlug || pkg.annual_slug === activeSlug
    );

    if (matchedActivePkg) {
      setActiveTaraPackage(matchedActivePkg);
      setCheckoutReturn('none');

      setTimeout(() => {
        setIsLoading(false);
        const queryParameters = new URLSearchParams(window.location.search);
        const orderId = queryParameters.get('orderId') as string;
        if (isNotEmptyString(orderId) && !isPlanLocked) {
          dispatch(
            setMessage({
              message: `${t('SubscriptionSuccessMainText')}`,
              messageType: 'success',
            })
          );
          removeQueryParameter('orderId');
        }
      }, 500);
    } else {
      setActiveTaraPackage(null);
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    }
  }, [
    availableTaraSubscriptionPackages,
    taraSubscriptionDetails,
    dispatch,
    t,
    isPlanLocked,
  ]);

  useEffect(() => {
    if (
      new URLSearchParams(window.location.search).get('checkout') ===
      'cancelled'
    ) {
      dispatch(
        setMessage({
          message: t('Checkout-Cancelled'),
          messageType: 'info',
        })
      );
      removeQueryParameter('checkout');
    }
    // Once per visit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Ask for the plan again until it shows, then say it will appear shortly.
  // The refetch is quiet (no app re-init, no splash), so this page stays
  // mounted and the round count in the ref keeps counting down.
  const checkoutAttemptsLeft = useRef(CHECKOUT_RETURN_ATTEMPTS);
  useEffect(() => {
    if (checkoutReturn !== 'waiting') return;
    let cancelled = false;
    const wait = () =>
      new Promise<void>((resolve) =>
        window.setTimeout(resolve, CHECKOUT_RETURN_INTERVAL_MS)
      );
    (async () => {
      while (checkoutAttemptsLeft.current > 0) {
        await wait();
        if (cancelled) return;
        checkoutAttemptsLeft.current -= 1;
        await refresh_tara_subscription_quietly();
        if (cancelled) return;
      }
      setCheckoutReturn((current) =>
        current === 'waiting' ? 'timedOut' : current
      );
    })();
    return () => {
      cancelled = true;
    };
  }, [checkoutReturn]);

  // Check again: one more quiet read, then the same message if still absent.
  const handleCheckoutCheckAgain = () => {
    checkoutAttemptsLeft.current = 1;
    setCheckoutReturn('waiting');
  };

  const handleChangePlan = () => {
    setShowChangePlan(true);
  };

  const handleSwitchToAnnual = async () => {
    if (!activeTaraPackage || !taraSubscriptionDetails) return;
    // One request per click, even before the button re-renders disabled.
    if (isSwitchToAnnualInFlight.current) return;
    isSwitchToAnnualInFlight.current = true;
    setSwitchToAnnualError(null);

    try {
      setIsSwitchToAnnualLoading(true);

      const currentSeatCount = taraSubscriptionDetails?.purchased_shops
        ?.seat_count
        ? parseInt(taraSubscriptionDetails.purchased_shops.seat_count)
        : undefined;

      const result = await request_package_subscription(
        activeTaraPackage.annual_slug,
        currentSeatCount,
        { hideServerToast: true }
      );

      if (!result.ok) {
        // Stop the spinner and say why nothing happened: the server's
        // reason when it gave one, under the button and in a toast.
        isSwitchToAnnualInFlight.current = false;
        setIsSwitchToAnnualLoading(false);
        const message = getCheckoutFailureMessage(
          result.message,
          t('Checkout-StartFailed')
        );
        setSwitchToAnnualError(message);
        dispatch(setMessage({ message, messageType: 'error' }));
        return;
      }

      const response = result.data;
      if (
        response &&
        response.payment_details &&
        response.payment_details.payment_details &&
        response.payment_details.payment_details.url
      ) {
        // Keep loading state active during redirect
        window.location.href = response.payment_details.payment_details.url;
      } else {
        isSwitchToAnnualInFlight.current = false;
        setIsSwitchToAnnualLoading(false);
      }
    } catch (error) {
      setSwitchToAnnualError(t('Checkout-StartFailed'));
      dispatch(
        setMessage({
          message: 'Error switching to annual subscription',
          messageType: 'error',
        })
      );
      isSwitchToAnnualInFlight.current = false;
      setIsSwitchToAnnualLoading(false);
    }
  };

  const resetCancelFlowState = () => {
    setCancelFlowOptions([]);
    setSurveyCancelFlowOptions([]);
    setActiveCancelFlowIndex(0);
    setCancelFlowError(null);
    setIsShowingQuestionnaire(false);
    setSelectedCancelReason('');
    setQuestionnaireStage('questionnaire');
    setActiveSurveyOption(null);
    setOtherToolsReason('');
    setOtherReason('');
    setFinalFeedback('');
    setSatisfactionRating(null);
    setIsTriggeringCancellation(false);
    setIsProcessingFollowUpAction(false);
  };

  const handleCancelSubscription = () => {
    if (!taraSubscriptionDetails?.id) return;
    resetCancelFlowState();
    setShowCancelFlow(true);
  };

  useEffect(() => {
    const fetchCancelFlowOptions = async () => {
      if (!showCancelFlow || !taraSubscriptionDetails?.id) return;

      setIsCancelSubscriptionLoading(true);
      setCancelFlowError(null);
      setCancelFlowOptions([]);
      setSurveyCancelFlowOptions([]);
      setActiveCancelFlowIndex(0);
      setIsShowingQuestionnaire(false);
      setSelectedCancelReason('');

      try {
        const response = await get_cancel_flow_options_api_call(
          taraSubscriptionDetails.id
        );

        if (!response?.options) {
          setIsShowingQuestionnaire(true);
          return;
        }

        const optionEntries = Object.entries(response.options);

        const filteredOffers: CancelFlowOption[] = [];
        const surveyOffers: CancelFlowOption[] = [];

        optionEntries.forEach(([key, option]) => {
          //! Since the BE is sending this -> must be removed from the API response
          if (key === 'direct_cancel') {
            return;
          }

          if (key.startsWith('survey')) {
            surveyOffers.push(option);
            return;
          }

          filteredOffers.push(option);
        });

        setCancelFlowOptions(filteredOffers);
        setSurveyCancelFlowOptions(surveyOffers);

        if (filteredOffers.length === 0) {
          setIsShowingQuestionnaire(true);
        }
      } catch (error) {
        setCancelFlowError(t('SubscriptionCancelFlowLoadError'));
        setIsShowingQuestionnaire(true);
      } finally {
        setIsCancelSubscriptionLoading(false);
      }
    };

    fetchCancelFlowOptions();
  }, [showCancelFlow, taraSubscriptionDetails?.id]);

  const handleCloseCancelFlow = () => {
    setShowCancelFlow(false);
    setIsCancelSubscriptionLoading(false);
    resetCancelFlowState();
  };

  const handleMoveToNextOffer = () => {
    if (activeCancelFlowIndex < cancelFlowOptions.length - 1) {
      setActiveCancelFlowIndex((prev) => prev + 1);
      return;
    }

    setQuestionnaireStage('questionnaire');
    setActiveSurveyOption(null);
    setIsShowingQuestionnaire(true);
  };

  const handleAcceptOffer = async (option: CancelFlowOption) => {
    if (!option?.type || !taraSubscriptionDetails?.id) return;
    try {
      setIsProcessingFollowUpAction(true);
      const response = await process_cancel_flow_action_api_call({
        subscription_id: taraSubscriptionDetails.id,
        action_type: option.type,
      });

      if (!response?.success) {
        throw new Error(response?.message || 'PROCESS_CANCEL_FLOW_FAILED');
      }

      dispatch(
        setMessage({
          message:
            response?.message ||
            t('SubscriptionCancelFlowActionSuccessMessage'),
          messageType: 'success',
        })
      );
      handleCloseCancelFlow();
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.message;
      const errorMessage =
        typeof apiMessage === 'string' && apiMessage.trim().length > 0
          ? apiMessage
          : t('SubscriptionCancelFlowActionErrorMessage');
      dispatch(
        setMessage({
          message: errorMessage,
          messageType: 'error',
        })
      );
      setIsProcessingFollowUpAction(false);
    }
  };

  const currentCancelFlowOption =
    !isShowingQuestionnaire && cancelFlowOptions.length > 0
      ? cancelFlowOptions[activeCancelFlowIndex]
      : null;

  const canGoBackToPreviousOffer =
    !isShowingQuestionnaire && activeCancelFlowIndex > 0;

  const canReturnFromQuestionnaire =
    isShowingQuestionnaire &&
    cancelFlowOptions.length > 0 &&
    questionnaireStage === 'questionnaire';

  const canGoBackWithinQuestionnaire =
    isShowingQuestionnaire && questionnaireStage !== 'questionnaire';

  const handleGoBackToPreviousOffer = () => {
    if (isShowingQuestionnaire) {
      if (questionnaireStage !== 'questionnaire') {
        setQuestionnaireStage('questionnaire');
        setActiveSurveyOption(null);
        return;
      }

      if (cancelFlowOptions.length > 0) {
        setIsShowingQuestionnaire(false);
        return;
      }
    }

    if (canGoBackToPreviousOffer) {
      setActiveCancelFlowIndex((prev) => Math.max(prev - 1, 0));
    }
  };

  const cancelSurveyOptions = useMemo(
    () => [
      {
        id: 'too_expensive',
        label: t('SubscriptionCancelFlowReasonTooExpensive'),
      },
      {
        id: 'technical_issues',
        label: t('SubscriptionCancelFlowReasonTechnicalIssues'),
      },
      {
        id: 'prefer_other_tools',
        label: t('SubscriptionCancelFlowReasonPreferOtherTools'),
      },
      {
        id: 'missing_features',
        label: t('SubscriptionCancelFlowReasonMissingFeatures'),
      },
      { id: 'other', label: t('SubscriptionCancelFlowReasonOther') },
    ],
    [t]
  );

  const handleContinueFromQuestionnaire = () => {
    switch (selectedCancelReason) {
      case 'too_expensive': {
        const offer = surveyCancelFlowOptions.find(
          (option) => option.type === 'survey_1_month_free'
        );
        if (offer) {
          setActiveSurveyOption(offer);
          setQuestionnaireStage('survey_offer');
        } else {
          setQuestionnaireStage('final');
        }
        break;
      }
      case 'technical_issues': {
        const supportOption = surveyCancelFlowOptions.find(
          (option) => option.type === 'survey_connect_direct_support'
        );
        if (supportOption) {
          setActiveSurveyOption(supportOption);
          setQuestionnaireStage('support');
        } else {
          setQuestionnaireStage('final');
        }
        break;
      }
      case 'prefer_other_tools': {
        setQuestionnaireStage('other_tools');
        break;
      }
      case 'missing_features': {
        setQuestionnaireStage('missing_features');
        break;
      }
      case 'other': {
        setQuestionnaireStage('other');
        break;
      }
      default: {
        setQuestionnaireStage('final');
      }
    }
  };

  const handleSurveyNoThanks = () => {
    setActiveSurveyOption(null);
    setQuestionnaireStage('final');
  };

  const handleAcceptSurveyOption = async (option: CancelFlowOption | null) => {
    if (!option?.type || !taraSubscriptionDetails?.id) return;
    try {
      setIsProcessingFollowUpAction(true);
      const response = await process_cancel_flow_action_api_call({
        subscription_id: taraSubscriptionDetails.id,
        action_type: option.type,
      });

      if (!response?.success) {
        throw new Error(response?.message || 'PROCESS_CANCEL_FLOW_FAILED');
      }

      dispatch(
        setMessage({
          message:
            response?.message ||
            t('SubscriptionCancelFlowActionSuccessMessage'),
          messageType: 'success',
        })
      );
      handleCloseCancelFlow();
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.message;
      const errorMessage =
        typeof apiMessage === 'string' && apiMessage.trim().length > 0
          ? apiMessage
          : t('SubscriptionCancelFlowActionErrorMessage');
      dispatch(
        setMessage({
          message: errorMessage,
          messageType: 'error',
        })
      );
      setIsProcessingFollowUpAction(false);
    }
  };

  const handleSupportYes = async (option: CancelFlowOption | null) => {
    if (!option?.type || !taraSubscriptionDetails?.id) return;
    try {
      setIsProcessingFollowUpAction(true);
      const response = await process_cancel_flow_action_api_call({
        subscription_id: taraSubscriptionDetails.id,
        action_type: option.type,
      });

      if (!response?.success) {
        throw new Error(response?.message || 'PROCESS_CANCEL_FLOW_FAILED');
      }

      dispatch(
        setMessage({
          message: t('SubscriptionCancelFlowSupportFallback'),
          messageType: 'success',
        })
      );
      handleCloseCancelFlow();
    } catch (error: any) {
      const apiMessage = error?.response?.data?.message || error?.message;
      const errorMessage =
        typeof apiMessage === 'string' && apiMessage.trim().length > 0
          ? apiMessage
          : t('SubscriptionCancelFlowActionErrorMessage');
      dispatch(
        setMessage({
          message: errorMessage,
          messageType: 'error',
        })
      );
      setIsProcessingFollowUpAction(false);
    }
  };

  const cancelFlowCurrencySymbol = useMemo(() => {
    if (!activeTaraPackage?.currency) return '';
    return getCurrencySymbol(activeTaraPackage.currency);
  }, [activeTaraPackage?.currency]);

  const renderRoleSpecificPackageItems = () => {
    // Only the agency per-seat plan is offered, whatever the organisation role.
    const rolePackages =
      availableTaraSubscriptionPackages?.structuredPackageData?.filter(
        (pkg: any) => pkg.role === 'agency'
      ) || [];

    if (rolePackages.length === 0) return null;

    const PackageItemComponent = AgencyPackageItem;
    const pricingHierarchy = tara_agency_pricing_hierarchy;
    // A plan outside the agency hierarchy (a legacy seller plan) ranks below
    // every agency plan, so each agency plan is an upgrade from it.
    const currentRank =
      pricingHierarchy[
        taraSubscriptionDetails?.package_slug as keyof typeof pricingHierarchy
      ] ?? 0;

    const activeItems: JSX.Element[] = [];
    const upgradableItems: JSX.Element[] = [];
    const disabledItems: JSX.Element[] = [];

    rolePackages.forEach((pkg: any) => {
      const monthlyIsActive =
        taraSubscriptionDetails?.package_slug === pkg.monthly_slug;
      const annualIsActive =
        taraSubscriptionDetails?.package_slug === pkg.annual_slug;

      const purchasedSeatCount =
        (monthlyIsActive || annualIsActive) &&
        taraSubscriptionDetails?.purchased_shops?.seat_count
          ? parseInt(taraSubscriptionDetails?.purchased_shops?.seat_count)
          : undefined;

      const monthlyIsUpgradable =
        currentRank <
        pricingHierarchy[pkg.monthly_slug as keyof typeof pricingHierarchy];

      const annualIsUpgradable =
        currentRank <
        pricingHierarchy[pkg.annual_slug as keyof typeof pricingHierarchy];

      const monthlyItem = (
        <PackageItemComponent
          key={`${pkg.id}-monthly`}
          isMonthly={true}
          isCancelMode={true}
          purchased_seat_count={purchasedSeatCount}
          packageDetails={{
            ...pkg,
            is_active: monthlyIsActive,
            subscription_id: monthlyIsActive
              ? taraSubscriptionDetails?.id
              : undefined,
            next_billing_date: monthlyIsActive
              ? taraSubscriptionDetails?.end_date
              : undefined,
            is_most_popular: false,
          }}
          isUpgradable={monthlyIsUpgradable}
        />
      );

      const annualItem = (
        <PackageItemComponent
          key={`${pkg.id}-annual`}
          isMonthly={false}
          isCancelMode={true}
          purchased_seat_count={purchasedSeatCount}
          packageDetails={{
            ...pkg,
            is_active: annualIsActive,
            subscription_id: annualIsActive
              ? taraSubscriptionDetails?.id
              : undefined,
            next_billing_date: annualIsActive
              ? taraSubscriptionDetails?.end_date
              : undefined,
            is_most_popular: false,
          }}
          isUpgradable={annualIsUpgradable}
        />
      );

      if (monthlyIsActive) {
        activeItems.push(monthlyItem);
      } else if (monthlyIsUpgradable) {
        upgradableItems.push(monthlyItem);
      } else {
        disabledItems.push(monthlyItem);
      }

      if (annualIsActive) {
        activeItems.push(annualItem);
      } else if (annualIsUpgradable) {
        upgradableItems.push(annualItem);
      } else {
        disabledItems.push(annualItem);
      }
    });

    // Return items in the order of active, upgradable, disabled
    return [...activeItems, ...upgradableItems, ...disabledItems];
  };

  const getSupportDescription = () => {
    if (activeSurveyOption?.description) {
      return activeSurveyOption.description;
    }
    return t('SubscriptionCancelFlowSupportFallback');
  };

  const handleTriggerCancellation = async () => {
    if (!taraSubscriptionDetails?.id) return;

    try {
      setIsTriggeringCancellation(true);
      if (questionnaireStage === 'other_tools' && otherToolsReason) {
        await submit_cancel_flow_survey_api_call({
          subscription_id: taraSubscriptionDetails.id,
          feedback: otherToolsReason,
          survey_type: 'cancel_flow',
        });
      }

      if (questionnaireStage === 'other' && otherReason) {
        await submit_cancel_flow_survey_api_call({
          subscription_id: taraSubscriptionDetails.id,
          feedback: otherReason,
          survey_type: 'cancel_flow',
        });
      }

      if (
        questionnaireStage === 'final' &&
        (finalFeedback || satisfactionRating !== null)
      ) {
        await submit_cancel_flow_survey_api_call({
          subscription_id: taraSubscriptionDetails.id,
          feedback: finalFeedback,
          survey_type: 'cancel_flow',
          satisfaction_rating: satisfactionRating ?? undefined,
        });
      }

      const response = await manage_package_subscriptions_api_call(
        taraSubscriptionDetails.id
      );

      if (response && response.data && response.data.portal_url) {
        window.location.href = response.data.portal_url;
      } else {
        setIsTriggeringCancellation(false);
      }
    } catch (error) {
      dispatch(
        setMessage({
          message: 'Error opening subscription management',
          messageType: 'error',
        })
      );
      setIsTriggeringCancellation(false);
    }
  };

  const satisfactionOptions = useMemo(
    () => [
      { value: 1, label: '😞' },
      { value: 2, label: '🙁' },
      { value: 3, label: '😐' },
      { value: 4, label: '🙂' },
      { value: 5, label: '😁' },
    ],
    []
  );

  const isMonthlyActive =
    activeTaraPackage &&
    taraSubscriptionDetails?.package_slug === activeTaraPackage.monthly_slug;

  const formatDate = (dateString: string) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const getBillingPeriod = () => {
    if (!activeTaraPackage || !taraSubscriptionDetails) return 'N/A';
    return isMonthlyActive ? 'Monthly' : 'Annual';
  };

  // Seat billing breakdown. An ADUP admin can grant an organisation free seats
  // ("20 seats, 5 free → pay 15"); Stripe is charged for purchased − free, so
  // the plan price below is derived from the BILLED count.
  const purchasedSeatCountValue = taraSubscriptionDetails?.purchased_shops
    ?.seat_count
    ? parseInt(taraSubscriptionDetails.purchased_shops.seat_count)
    : 1;
  const freeSeatCount = Number(taraSubscriptionDetails?.free_seats ?? 0);
  // `billed_seats` is authoritative when it is a REAL count (>= 1, matching the
  // floor Stripe is charged with). The API returns 0 when the subscription has
  // no seat_count at all — treating that as a price basis renders "€0/month",
  // so 0 must fall through to the purchased count, which is why this is `||`
  // and NOT `??` (`??` only falls back on null/undefined, never on 0).
  const rawBilledSeats =
    taraSubscriptionDetails?.billed_seats != null
      ? Number(taraSubscriptionDetails.billed_seats)
      : undefined;
  const billedSeatCount =
    rawBilledSeats && rawBilledSeats > 0 ? rawBilledSeats : undefined;

  const isAgencyPlanActive = activeTaraPackage?.role === 'agency';

  const getPrice = () => {
    if (!activeTaraPackage) return { amount: '0', currency: '€' };

    // Priced by the type of the active package, not the organisation role.
    if (isAgencyPlanActive) {
      // Agency pricing logic — priced on the BILLED seat count, not the
      // purchased one. An ADUP admin can grant free seats, and Stripe charges
      // purchased − free; showing the purchased total here would quote a price
      // the customer is never actually charged.
      // `billed_seats` comes from the API (already floored at 1); fall back to
      // the purchased count when the field is absent (older API response).
      const currentSeatCount = billedSeatCount ?? purchasedSeatCountValue;

      if (isMonthlyActive) {
        const totalPrice =
          activeTaraPackage.monthly_per_seat_price_number * currentSeatCount;
        return {
          amount: `${getCurrencySymbol(
            activeTaraPackage.currency
          )}${totalPrice}`,
          currency: activeTaraPackage.currency,
        };
      } else {
        const totalPrice =
          activeTaraPackage.annual_per_seat_price_number *
          currentSeatCount *
          12;
        return {
          amount: `${getCurrencySymbol(
            activeTaraPackage.currency
          )}${totalPrice}`,
          currency: activeTaraPackage.currency,
        };
      }
    } else {
      // Seller pricing logic - use direct price
      return isMonthlyActive
        ? {
            amount: activeTaraPackage.monthly_price,
            currency: activeTaraPackage.currency,
          }
        : {
            amount: activeTaraPackage.annual_price,
            currency: activeTaraPackage.currency,
          };
    }
  };

  const cancelFlowPopupStyles = useMemo(
    () => ({
      width: '90%',
      maxWidth:
        isShowingQuestionnaire && questionnaireStage === 'missing_features'
          ? '1040px'
          : '640px',
      maxHeight: '90vh',
      overflow: 'auto',
    }),
    [isShowingQuestionnaire, questionnaireStage]
  );

  return (
    <>
      <SubPageInnerContentHolder
        title={t('Subscription')}
        backBtnLink={isPlanLocked ? undefined : HOME_ROUTE}
        /* ------Help Button------ */
        mainButtonType='white'
        mainBtnTitle={t('Help')}
        mainBtnIcon={questionSVG}
        mainBtnLoading={false}
        mainBtnLink={HELP_LINKS.settings.subscription}
        isMainBtnLinkExternal={true}
      >
        <div className='settings_mobile-only-top-margin'></div>
        <PlanLockNotice payment={payment} />
        {checkoutReturn === 'waiting' && (
          <div
            className='settingsInfoBanner subscriptionSettings__checkoutReturn'
            style={{ marginBottom: '2rem' }}
            role='status'
            data-testid='checkout-return-processing'
          >
            {t('Checkout-ReturnProcessing')}
          </div>
        )}
        {checkoutReturn === 'timedOut' && (
          <div
            className='settingsInfoBanner subscriptionSettings__checkoutReturn'
            style={{ marginBottom: '2rem' }}
            role='status'
            data-testid='checkout-return-timeout'
          >
            <strong>{t('Checkout-ReturnTimeoutTitle')}</strong>{' '}
            {t('Checkout-ReturnTimeout')}{' '}
            <MainSquareButton
              value={t('PlanLock-CheckAgain')}
              type='button'
              onClick={handleCheckoutCheckAgain}
              buttonType='light'
            />
          </div>
        )}
        {/* Phase 12 — Usage section (agency: clients; enterprise: seats). The
            Stripe subscription display below stays intact; this only adds a
            read-only summary above it. */}
        <RoleGate capability='billing.view'>
          <BillingUsageSection />
        </RoleGate>
        {/* Billing e-mail: the server decides who sees and who edits it, and
            the field hides itself when its settings cannot be loaded. */}
        <BillingEmailField />
        {isLoading ? (
          <LoadingSpinner />
        ) : isPaymentPending ? null : activeTaraPackage && !isPlanLocked ? (
          <SettingsCommonCard
            mainTitle={t('CurrentPlan')}
            mainIconSvg={simpleCreditCardSVG}
            mainIconBgColor='transparent'
            mainIconSvgFillColor='transparent'
          >
            <div className='subscriptionSettingsCurrentPlan'>
              <div className='subscriptionSettingsCurrentPlan__details'>
                <div className='subscriptionSettingsCurrentPlan__details--left'>
                  <h3 className='subscriptionSettingsCurrentPlan__details--left-title'>
                    {activeTaraPackage.name}
                  </h3>
                  <p className='subscriptionSettingsCurrentPlan__details--left-subtitle'>
                    {activeTaraPackage.description}
                  </p>
                  <div className='subscriptionSettingsCurrentPlan__details--left-price'>
                    <h2>{getPrice().amount}</h2>
                    <span>/{isMonthlyActive ? 'month' : 'year'}</span>
                  </div>
                  <div className='subscriptionSettingsCurrentPlan__details--left-item'>
                    <p>Billing:</p>
                    <span>{getBillingPeriod()}</span>
                  </div>
                  {isAgencyPlanActive && (
                    <div
                      className='subscriptionSettingsCurrentPlan__details--left-item'
                      data-testid='current-plan-seats'
                    >
                      <p>{t('CurrentPlan-SeatsLabel')}:</p>
                      <span>
                        {freeSeatCount > 0
                          ? t('CurrentPlan-SeatsWithFree', {
                              total: purchasedSeatCountValue,
                              billed:
                                billedSeatCount ?? purchasedSeatCountValue,
                              free: freeSeatCount,
                            })
                          : t('CurrentPlan-Seats', {
                              total: purchasedSeatCountValue,
                            })}
                      </span>
                    </div>
                  )}
                  <div className='subscriptionSettingsCurrentPlan__details--left-item'>
                    <p>Next renewal:</p>
                    <span>{formatDate(taraSubscriptionDetails?.end_date)}</span>
                  </div>
                  <div className='subscriptionSettingsCurrentPlan__details--left-item'>
                    <p>Auto-renewal:</p>
                    <span>Enabled</span>
                  </div>
                </div>
                <div className='subscriptionSettingsCurrentPlan__details--right'>
                  <div className='subscriptionSettingsCurrentPlan__details--right-status'>
                    <div>Active</div>
                  </div>
                </div>
              </div>
              {/* W1 — Stripe management actions are owner-only
                  (`billing.manage`). team_lead / manager / read_only retain
                  the read-only plan + usage view above; the management
                  buttons are hidden with a short note. */}
              <RoleGate
                capability='billing.manage'
                fallback={
                  <div className='subscriptionSettingsCurrentPlan__actions'>
                    <p className='subscriptionSettingsCurrentPlan__readonly-note'>
                      {t('BillingManageReadOnlyNotice')}
                    </p>
                  </div>
                }
              >
                <div className='subscriptionSettingsCurrentPlan__actions'>
                  <MainSquareButton
                    value={t('ChangePlan')}
                    type='button'
                    buttonType='white'
                    disabled={isManageLoading}
                    onClick={handleChangePlan}
                  />
                  {isMonthlyActive && (
                    <MainSquareButton
                      value={t('SwitchToAnnual')}
                      type='button'
                      buttonType='white'
                      disabled={isSwitchToAnnualLoading}
                      loading={isSwitchToAnnualLoading}
                      onClick={handleSwitchToAnnual}
                    />
                  )}
                  <MainSquareButton
                    value={t('CancelSubscription')}
                    type='button'
                    buttonType='white'
                    disabled={isCancelSubscriptionLoading}
                    loading={isCancelSubscriptionLoading}
                    onClick={() => {
                      APP_ENV === 'DEV'
                        ? handleCancelSubscription()
                        : handleTriggerCancellation(); //? Cancellation flow hidden for v2.4 release
                    }}
                    classes='subscriptionSettingsCurrentPlan__actions--cancel'
                  />
                </div>
                {switchToAnnualError && (
                  <p
                    className='subscriptionSettingsCurrentPlan__error'
                    role='alert'
                    data-testid='switch-to-annual-error'
                    style={{ color: '#d32f2f', fontSize: '1.3rem' }}
                  >
                    {switchToAnnualError}
                  </p>
                )}
              </RoleGate>
            </div>
          </SettingsCommonCard>
        ) : taraSubscriptionDetails?.currentSubscriptionStatus === 'active' &&
          !isPlanLocked ? (
          // Subscription exists but matches no package in this role's
          // catalogue: an admin-activated trial (`beginner-trial`) or an
          // admin-granted legacy plan. Show its details instead of the
          // package picker pretending there is no plan.
          <>
            <SettingsCommonCard
              mainTitle={t('CurrentPlan')}
              mainIconSvg={simpleCreditCardSVG}
              mainIconBgColor='transparent'
              mainIconSvgFillColor='transparent'
            >
              <div className='subscriptionSettingsCurrentPlan'>
                <div className='subscriptionSettingsCurrentPlan__details'>
                  <div className='subscriptionSettingsCurrentPlan__details--left'>
                    <h3 className='subscriptionSettingsCurrentPlan__details--left-title'>
                      {taraSubscriptionDetails?.package ||
                        (taraSubscriptionDetails?.is_trial
                          ? t('TrialActive')
                          : t('CurrentPlan'))}
                    </h3>
                    {taraSubscriptionDetails?.is_trial && (
                      <div className='subscriptionSettingsCurrentPlan__details--left-price'>
                        <h2>€0</h2>
                        <span>/trial</span>
                      </div>
                    )}
                    <div className='subscriptionSettingsCurrentPlan__details--left-item'>
                      <p>Started:</p>
                      <span>
                        {formatDate(taraSubscriptionDetails?.start_date)}
                      </span>
                    </div>
                    <div className='subscriptionSettingsCurrentPlan__details--left-item'>
                      <p>
                        {taraSubscriptionDetails?.is_trial
                          ? 'Trial ends:'
                          : 'Next renewal:'}
                      </p>
                      <span>
                        {formatDate(taraSubscriptionDetails?.end_date)}
                      </span>
                    </div>
                    {taraSubscriptionDetails?.is_trial &&
                      taraSubscriptionDetails?.available_days != null && (
                        <div className='subscriptionSettingsCurrentPlan__details--left-item'>
                          <p>Days remaining:</p>
                          <span>{taraSubscriptionDetails.available_days}</span>
                        </div>
                      )}
                  </div>
                  <div className='subscriptionSettingsCurrentPlan__details--right'>
                    <div className='subscriptionSettingsCurrentPlan__details--right-status'>
                      <div>
                        {taraSubscriptionDetails?.is_trial ? 'Trial' : 'Active'}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </SettingsCommonCard>
            {/* A trial should still be able to pick a real plan. */}
            {taraSubscriptionDetails?.is_trial && (
              <RoleGate capability='billing.manage'>
                <div className='subscriptionSettings__planPicker'>
                  <SubscriptionPackages
                    taraSubscriptionPackages={
                      availableTaraSubscriptionPackages?.structuredPackageData?.filter(
                        (pkg: any) => pkg.role === 'agency'
                      ) || []
                    }
                  />
                </div>
              </RoleGate>
            )}
          </>
        ) : (
          // No active package — the package picker initiates a Stripe
          // purchase, so it's a `billing.manage` action. Non-managers see a
          // read-only note instead of the buy flow.
          <RoleGate
            capability='billing.manage'
            fallback={
              <div className='subscriptionSettings__planPicker'>
                <p className='subscriptionSettingsCurrentPlan__readonly-note'>
                  {t('BillingManageReadOnlyNotice')}
                </p>
              </div>
            }
          >
            <div
              className='subscriptionSettings__planPicker'
              data-testid='subscription-plan-picker'
            >
              <SubscriptionPackages
                taraSubscriptionPackages={
                  availableTaraSubscriptionPackages?.structuredPackageData?.filter(
                    (pkg: any) => pkg.role === 'agency'
                  ) || []
                }
              />
            </div>
          </RoleGate>
        )}
      </SubPageInnerContentHolder>

      {/* Change Plan Popup */}
      <Popup
        trigger={showChangePlan}
        setTrigger={() => setShowChangePlan(false)}
        styles={{
          width: '90%',
          maxWidth: '1200px',
          maxHeight: '90vh',
          overflow: 'auto',
        }}
      >
        <div style={{ padding: '1rem' }}>
          <div
            className='agencyPackages'
            style={{
              justifyItems: 'center',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              minHeight: '400px',
            }}
          >
            <div
              className='agencyPackages__items'
              style={{
                flexWrap: 'wrap',
                justifyContent: 'space-evenly',
              }}
            >
              {renderRoleSpecificPackageItems()}
            </div>
          </div>
        </div>
      </Popup>

      {/* Cancel Flow Popup */}
      <Popup
        trigger={showCancelFlow}
        setTrigger={handleCloseCancelFlow}
        styles={cancelFlowPopupStyles}
      >
        <div className='subscriptionCancelFlow'>
          {isCancelSubscriptionLoading ? (
            <div className='subscriptionCancelFlow__loader'>
              <LoadingSpinner />
            </div>
          ) : (
            <>
              {cancelFlowError ? (
                <div className='subscriptionCancelFlow__error'>
                  <p>{cancelFlowError}</p>
                </div>
              ) : currentCancelFlowOption ? (
                <>
                  <div className='subscriptionCancelFlow__header'>
                    <h2>{t('SubscriptionCancelFlowTitle')}</h2>
                    <p>{t('SubscriptionCancelFlowSubtitle')}</p>
                  </div>
                  <div className='subscriptionCancelFlow__option'>
                    <div className='subscriptionCancelFlow__option-body'>
                      <div className='subscriptionCancelFlow__option-meta'>
                        <span className='subscriptionCancelFlow__option-chip'>
                          {t('SubscriptionCancelFlowOption')}
                        </span>
                      </div>
                      <h3>{currentCancelFlowOption.title}</h3>
                      {currentCancelFlowOption.description && (
                        <p>{currentCancelFlowOption.description}</p>
                      )}

                      {currentCancelFlowOption.type === 'discount_3_months' &&
                        currentCancelFlowOption.discounted_price !==
                          undefined && (
                          <div className='subscriptionCancelFlow__pricing'>
                            {'original_price' in currentCancelFlowOption &&
                              currentCancelFlowOption.original_price !==
                                undefined && (
                                <span className='subscriptionCancelFlow__pricing-original'>
                                  {`${cancelFlowCurrencySymbol}${currentCancelFlowOption.original_price}`}
                                </span>
                              )}
                            <span className='subscriptionCancelFlow__pricing-discounted'>
                              {`${cancelFlowCurrencySymbol}${currentCancelFlowOption.discounted_price}`}
                            </span>
                          </div>
                        )}

                      {currentCancelFlowOption.type !== 'discount_3_months' &&
                        'discounted_price' in currentCancelFlowOption &&
                        currentCancelFlowOption.discounted_price !==
                          undefined && (
                          <div className='subscriptionCancelFlow__option-highlight'>
                            <span>
                              {t('SubscriptionCancelFlowDiscountedPrice')}
                            </span>
                            <strong>
                              {currentCancelFlowOption.discounted_price}
                            </strong>
                          </div>
                        )}

                      {'free_access_until' in currentCancelFlowOption &&
                        currentCancelFlowOption.free_access_until && (
                          <div className='subscriptionCancelFlow__option-highlight'>
                            <span>
                              {t('SubscriptionCancelFlowFreeAccessUntil')}
                            </span>
                            <strong>
                              {currentCancelFlowOption.free_access_until}
                            </strong>
                          </div>
                        )}
                    </div>
                  </div>

                  <div className='subscriptionCancelFlow__actions'>
                    <div className='subscriptionCancelFlow__actions-left'>
                      {(canGoBackToPreviousOffer ||
                        canReturnFromQuestionnaire ||
                        canGoBackWithinQuestionnaire) && (
                        <button
                          type='button'
                          className='subscriptionCancelFlow__back'
                          onClick={handleGoBackToPreviousOffer}
                        >
                          {t('Go Back')}
                        </button>
                      )}
                    </div>
                    <div className='subscriptionCancelFlow__actions-right'>
                      <button
                        type='button'
                        className='subscriptionCancelFlow__skip'
                        onClick={handleMoveToNextOffer}
                      >
                        {t('SubscriptionCancelFlowNoThanks')}
                      </button>
                      <MainSquareButton
                        value={t('SubscriptionCancelFlowAcceptDeal')}
                        type='button'
                        loading={isProcessingFollowUpAction}
                        disabled={isProcessingFollowUpAction}
                        onClick={() =>
                          handleAcceptOffer(currentCancelFlowOption)
                        }
                        classes='subscriptionCancelFlow__accept'
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className='subscriptionCancelFlow__questionnaire'>
                  <div className='subscriptionCancelFlow__header'>
                    {questionnaireStage === 'questionnaire' && (
                      <>
                        <h2>{t('SubscriptionCancelFlowQuestionnaireTitle')}</h2>
                        <p>
                          {t('SubscriptionCancelFlowQuestionnaireSubtitle')}
                        </p>
                      </>
                    )}
                    {questionnaireStage === 'survey_offer' &&
                      activeSurveyOption && (
                        <>
                          <h2>{t('SubscriptionCancelFlowTitle')}</h2>
                          <p>{t('SubscriptionCancelFlowSubtitle')}</p>
                        </>
                      )}
                    {questionnaireStage === 'support' && activeSurveyOption && (
                      <>
                        <h2>{activeSurveyOption.title}</h2>
                        <p>{getSupportDescription()}</p>
                      </>
                    )}
                    {questionnaireStage === 'other_tools' && (
                      <>
                        <h2>{t('SubscriptionCancelFlowOtherToolsTitle')}</h2>
                        <p>{t('SubscriptionCancelFlowOtherToolsSubtitle')}</p>
                      </>
                    )}
                    {questionnaireStage === 'missing_features' && (
                      <>
                        <h2>
                          {t('SubscriptionCancelFlowMissingFeaturesTitle')}
                        </h2>
                        <p>
                          {t('SubscriptionCancelFlowMissingFeaturesSubtitle')}
                        </p>
                      </>
                    )}
                    {questionnaireStage === 'other' && (
                      <>
                        <h2>{t('SubscriptionCancelFlowOtherTitle')}</h2>
                        <p>{t('SubscriptionCancelFlowOtherSubtitle')}</p>
                      </>
                    )}
                    {questionnaireStage === 'final' && (
                      <>
                        <h2>{t('SubscriptionCancelFlowFinalTitle')}</h2>
                        <p>{t('SubscriptionCancelFlowFinalSubtitle')}</p>
                      </>
                    )}
                  </div>

                  {questionnaireStage === 'questionnaire' && (
                    <>
                      <div className='subscriptionCancelFlow__questionnaire-options'>
                        {cancelSurveyOptions.map((option) => (
                          <label
                            key={option.id}
                            className='subscriptionCancelFlow__questionnaire-option'
                          >
                            <input
                              type='radio'
                              name='cancel-reason'
                              value={option.id}
                              checked={selectedCancelReason === option.id}
                              onChange={(event) =>
                                setSelectedCancelReason(event.target.value)
                              }
                            />
                            <span>{option.label}</span>
                          </label>
                        ))}
                      </div>

                      <div className='subscriptionCancelFlow__questionnaire-actions'>
                        <div className='subscriptionCancelFlow__actions-left'>
                          {canReturnFromQuestionnaire && (
                            <button
                              type='button'
                              className='subscriptionCancelFlow__back'
                              onClick={handleGoBackToPreviousOffer}
                            >
                              {t('Go Back')}
                            </button>
                          )}
                        </div>
                        <div className='subscriptionCancelFlow__actions-right'>
                          <MainSquareButton
                            value={t('SubscriptionCancelFlowContinue')}
                            type='button'
                            disabled={!selectedCancelReason}
                            onClick={handleContinueFromQuestionnaire}
                            classes='subscriptionCancelFlow__submit'
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {questionnaireStage === 'survey_offer' && activeSurveyOption && (
                    <>
                      <div className='subscriptionCancelFlow__option subscriptionCancelFlow__option--survey'>
                        <div className='subscriptionCancelFlow__option-body'>
                          <div className='subscriptionCancelFlow__option-meta'>
                            <span className='subscriptionCancelFlow__option-chip'>
                              {t('SubscriptionCancelFlowOption')}
                            </span>
                          </div>
                          {(() => {
                            const freeAccessUntil =
                              'free_access_until' in activeSurveyOption
                                ? (activeSurveyOption.free_access_until as
                                    | string
                                    | undefined)
                                : undefined;
                            const originalPrice =
                              'original_price' in activeSurveyOption &&
                              activeSurveyOption.original_price !== undefined &&
                              activeSurveyOption.original_price !== null
                                ? String(activeSurveyOption.original_price)
                                : '';
                            return (
                              <>
                                <h3>
                                  {activeSurveyOption.title}
                                  {freeAccessUntil
                                    ? ` ${t(
                                        'SubscriptionCancelFlowUntilLabel'
                                      )} ${freeAccessUntil}`
                                    : ''}
                                </h3>
                                {activeSurveyOption.description && (
                                  <p>{activeSurveyOption.description}</p>
                                )}
                                <div className='subscriptionCancelFlow__pricing'>
                                  <span className='subscriptionCancelFlow__pricing-original'>
                                    {cancelFlowCurrencySymbol}
                                    {originalPrice}
                                  </span>
                                  <span className='subscriptionCancelFlow__pricing-discounted'>
                                    {cancelFlowCurrencySymbol}0
                                  </span>
                                </div>
                              </>
                            );
                          })()}
                        </div>
                      </div>
                      <div className='subscriptionCancelFlow__questionnaire-actions'>
                        <div className='subscriptionCancelFlow__actions-left'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__back'
                            onClick={() => {
                              setQuestionnaireStage('questionnaire');
                              setActiveSurveyOption(null);
                            }}
                          >
                            {t('Go Back')}
                          </button>
                        </div>
                        <div className='subscriptionCancelFlow__actions-right'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__skip'
                            onClick={handleSurveyNoThanks}
                          >
                            {t('SubscriptionCancelFlowNoThanks')}
                          </button>
                          <MainSquareButton
                            value={t('SubscriptionCancelFlowAcceptDeal')}
                            type='button'
                            loading={isProcessingFollowUpAction}
                            disabled={isProcessingFollowUpAction}
                            onClick={() =>
                              handleAcceptSurveyOption(activeSurveyOption)
                            }
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {questionnaireStage === 'support' && activeSurveyOption && (
                    <>
                      <div className='subscriptionCancelFlow__questionnaire-actions'>
                        <div className='subscriptionCancelFlow__actions-left'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__back'
                            onClick={() => {
                              setQuestionnaireStage('questionnaire');
                              setActiveSurveyOption(null);
                            }}
                          >
                            {t('Go Back')}
                          </button>
                        </div>
                        <div className='subscriptionCancelFlow__actions-right'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__skip'
                            onClick={handleSurveyNoThanks}
                          >
                            {t('SubscriptionCancelFlowNoThanks')}
                          </button>
                          <MainSquareButton
                            value={t('SubscriptionCancelFlowSupportYes')}
                            type='button'
                            loading={isProcessingFollowUpAction}
                            disabled={isProcessingFollowUpAction}
                            onClick={() => handleSupportYes(activeSurveyOption)}
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {questionnaireStage === 'other_tools' && (
                    <>
                      <textarea
                        className='subscriptionCancelFlow__textarea'
                        value={otherToolsReason}
                        onChange={(event) =>
                          setOtherToolsReason(event.target.value)
                        }
                        placeholder={t(
                          'SubscriptionCancelFlowOtherToolsPlaceholder'
                        )}
                      />
                      <div className='subscriptionCancelFlow__questionnaire-actions'>
                        <div className='subscriptionCancelFlow__actions-left'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__back'
                            onClick={() =>
                              setQuestionnaireStage('questionnaire')
                            }
                          >
                            {t('Go Back')}
                          </button>
                        </div>
                        <div className='subscriptionCancelFlow__actions-right'>
                          <MainSquareButton
                            value={t(
                              'SubscriptionCancelFlowConfirmCancellation'
                            )}
                            type='button'
                            disabled={
                              !otherToolsReason || isTriggeringCancellation
                            }
                            loading={isTriggeringCancellation}
                            onClick={handleTriggerCancellation}
                            buttonType='white'
                            classes='subscriptionCancelFlow__submit subscriptionSettingsCurrentPlan__actions--cancel'
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {questionnaireStage === 'missing_features' && (
                    <>
                      <div
                        className='agencyPackages__items subscriptionCancelFlow__plans'
                        style={{
                          flexWrap: 'wrap',
                          justifyContent: 'space-evenly',
                        }}
                      >
                        {renderRoleSpecificPackageItems()}
                      </div>
                      <div className='subscriptionCancelFlow__questionnaire-actions'>
                        <div className='subscriptionCancelFlow__actions-left'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__back'
                            onClick={() =>
                              setQuestionnaireStage('questionnaire')
                            }
                          >
                            {t('Go Back')}
                          </button>
                        </div>
                        <div className='subscriptionCancelFlow__actions-right'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__skip'
                            onClick={handleSurveyNoThanks}
                          >
                            {t('SubscriptionCancelFlowNoThanks')}
                          </button>
                        </div>
                      </div>
                    </>
                  )}

                  {questionnaireStage === 'other' && (
                    <>
                      <textarea
                        className='subscriptionCancelFlow__textarea'
                        value={otherReason}
                        onChange={(event) => setOtherReason(event.target.value)}
                        placeholder={t(
                          'SubscriptionCancelFlowOtherPlaceholder'
                        )}
                      />
                      <div className='subscriptionCancelFlow__questionnaire-actions'>
                        <div className='subscriptionCancelFlow__actions-left'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__back'
                            onClick={() =>
                              setQuestionnaireStage('questionnaire')
                            }
                          >
                            {t('Go Back')}
                          </button>
                        </div>
                        <div className='subscriptionCancelFlow__actions-right'>
                          <MainSquareButton
                            value={t(
                              'SubscriptionCancelFlowConfirmCancellation'
                            )}
                            type='button'
                            disabled={!otherReason || isTriggeringCancellation}
                            loading={isTriggeringCancellation}
                            onClick={handleTriggerCancellation}
                            buttonType='white'
                            classes='subscriptionCancelFlow__submit subscriptionSettingsCurrentPlan__actions--cancel'
                          />
                        </div>
                      </div>
                    </>
                  )}

                  {questionnaireStage === 'final' && (
                    <div className='subscriptionCancelFlow__final'>
                      <div className='subscriptionCancelFlow__rating'>
                        <p>{t('SubscriptionCancelFlowFinalRatingLabel')}</p>
                        <div className='subscriptionCancelFlow__rating-options'>
                          {satisfactionOptions.map((option) => (
                            <button
                              key={option.value}
                              type='button'
                              className='subscriptionCancelFlow__rating-option'
                              data-selected={
                                satisfactionRating === option.value
                                  ? 'true'
                                  : 'false'
                              }
                              onClick={() =>
                                setSatisfactionRating(option.value)
                              }
                            >
                              <span>{option.label}</span>
                            </button>
                          ))}
                        </div>
                      </div>
                      <textarea
                        className='subscriptionCancelFlow__textarea'
                        value={finalFeedback}
                        onChange={(event) =>
                          setFinalFeedback(event.target.value)
                        }
                        placeholder={t(
                          'SubscriptionCancelFlowFinalFeedbackPlaceholder'
                        )}
                      />
                      <div className='subscriptionCancelFlow__questionnaire-actions'>
                        <div className='subscriptionCancelFlow__actions-left'>
                          <button
                            type='button'
                            className='subscriptionCancelFlow__back'
                            onClick={() =>
                              setQuestionnaireStage('questionnaire')
                            }
                          >
                            {t('Go Back')}
                          </button>
                        </div>
                        <div className='subscriptionCancelFlow__actions-right'>
                          <MainSquareButton
                            value={t(
                              'SubscriptionCancelFlowConfirmCancellation'
                            )}
                            type='button'
                            loading={isTriggeringCancellation}
                            onClick={handleTriggerCancellation}
                            buttonType='white'
                            classes='subscriptionCancelFlow__submit subscriptionSettingsCurrentPlan__actions--cancel'
                          />
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Popup>
    </>
  );
};

export default SubscriptionSettings;
